import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import { useLocalization } from '@/contexts/LocalizationContext'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  Building2,
  Briefcase,
  ChevronDown,
  Check,
  ArrowRightLeft,
} from 'lucide-react'

export function ProfileSwitcher() {
  const {
    activeMode,
    canSwitchProfiles,
    entityProfile,
    businessClientProfile,
    switchToEntity,
    switchToBusinessClient,
    isBusinessClientMode,
  } = useProfileSwitch()
  const { t } = useLocalization()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  if (!canSwitchProfiles) return null

  const handleSwitch = (mode: 'entity' | 'business_client') => {
    if (mode === activeMode) {
      setIsOpen(false)
      return
    }

    if (mode === 'business_client') {
      switchToBusinessClient()
      navigate('/', { replace: true })
    } else {
      switchToEntity()
      navigate('/', { replace: true })
    }
    setIsOpen(false)
  }

  const activeLabel = isBusinessClientMode
    ? t('profileSwitch.businessClient')
    : t('profileSwitch.entityProfile')

  const activeIcon = isBusinessClientMode ? Briefcase : Building2

  const ActiveIcon = activeIcon

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 gap-2 border-dashed transition-all duration-200',
            isBusinessClientMode
              ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:border-amber-400'
              : 'border-gray-300 hover:bg-gray-50'
          )}
        >
          <ActiveIcon className="h-4 w-4 flex-shrink-0" />
          <span className="hidden sm:inline text-sm font-medium truncate max-w-[140px]">
            {activeLabel}
          </span>
          <ArrowRightLeft className="h-3 w-3 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[300px] p-0 overflow-hidden"
        align="start"
        sideOffset={8}
      >
        <div className="px-4 py-3 border-b bg-gradient-to-r from-gray-50 to-white">
          <p className="text-sm font-semibold text-gray-900">
            {t('profileSwitch.switchProfile')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('profileSwitch.switchDescription')}
          </p>
        </div>

        <div className="p-2 space-y-1">
          {/* Entity Profile Option */}
          <button
            onClick={() => handleSwitch('entity')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-start transition-all duration-150',
              'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
              activeMode === 'entity' && 'bg-brand-primary/5 ring-1 ring-brand-primary/20'
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0 transition-colors',
                activeMode === 'entity'
                  ? 'bg-gradient-to-br from-brand-primary to-brand-secondary text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500'
              )}
            >
              <Building2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  'text-sm font-medium truncate',
                  activeMode === 'entity' ? 'text-brand-primary' : 'text-gray-900'
                )}
              >
                {entityProfile?.display_name || t('profileSwitch.entityProfile')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('profileSwitch.entityDescription')}
              </p>
            </div>
            {activeMode === 'entity' && (
              <Check className="h-4 w-4 text-brand-primary flex-shrink-0" />
            )}
          </button>

          {/* Business Client Profile Option */}
          <button
            onClick={() => handleSwitch('business_client')}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-start transition-all duration-150',
              'hover:bg-amber-50 focus:bg-amber-50 focus:outline-none',
              activeMode === 'business_client' &&
                'bg-amber-50 ring-1 ring-amber-300/50'
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0 transition-colors',
                activeMode === 'business_client'
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500'
              )}
            >
              <Briefcase className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  'text-sm font-medium truncate',
                  activeMode === 'business_client' ? 'text-amber-700' : 'text-gray-900'
                )}
              >
                {businessClientProfile?.display_name || t('profileSwitch.businessClient')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('profileSwitch.businessClientDescription')}
              </p>
            </div>
            {activeMode === 'business_client' && (
              <Check className="h-4 w-4 text-amber-600 flex-shrink-0" />
            )}
          </button>
        </div>

        {isBusinessClientMode && (
          <div className="px-4 py-2.5 border-t bg-amber-50/50">
            <p className="text-[11px] text-amber-700 leading-relaxed">
              {t('profileSwitch.businessClientNotice')}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
